<template>
  <div class="layout-container grid h-screen w-screen">
    <SidebarProvider>
      <Sidebar />
      <ViewPort>
        <TopBanner />
        <HeaderToolbar>
          <SidebarTrigger />
          <div class="flex items-center">
            <Search v-if="currentUserStore.isAdmin() || currentUserStore.isContributor()" class="w-full p-4" />
            <router-link to="/contacts/calendar" class="text-gray-600 flex items-center mr-3 ml-6">
              <CalendarIcon />
            </router-link>
            <router-link to="/inbox" class="icon-link group">
              <Inbox />
              <div v-if="currentUserStore.currentUserAccountDetails.unreadMessages > 0" class="notification">
                {{ currentUserStore.currentUserAccountDetails.unreadMessages }}
              </div>
            </router-link>
            <router-link to="/tasks" class="icon-link group">
              <Bell />
              <div v-if="currentUserStore.currentUserAccountDetails.incompleteTasks > 0" class="notification">
                {{ currentUserStore.currentUserAccountDetails.incompleteTasks }}
              </div>
            </router-link>
          </div>
        </HeaderToolbar>

        <div class="flex-grow p-8">
          <div v-if="!hideRouteHeader" class="flex items-end justify-between route-header-section">
            <div class="flex items-center">
              <h1 v-if="routeHeader" class="mr-2 text-2xl font-semibold text-gray-900">
                {{ routeHeader }}
              </h1>
              <slot name="title-adjacent" />
              <Help v-if="showHelp()" class="mr-2" />
              <LoadingSpinner />
            </div>
            <div class="flex-grow text-right">
              <slot name="header"></slot>
            </div>
          </div>
          <div class="text-gray-500 text-md">
            <slot name="subheader"></slot>
          </div>

          <div class="mt-6">
            <slot v-if="ready"></slot>
          </div>
        </div>
      </ViewPort>
    </SidebarProvider>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCurrentUserStore } from '@/stores/currentUser';
import { useTeamStore } from '@/stores/team';
import Sidebar from './Sidebar/index.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import Search from '@/components/shared/Search.vue';
import Help from '@/components/shared/Help.vue';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import HeaderToolbar from '../HeaderToolbar.vue';
import CalendarIcon from '@/components/shared/icons/CalendarIcon.vue';
import { Bell, Inbox } from 'lucide-vue-next';
import ViewPort from '../ViewPort.vue';
import TopBanner from '@/lib/noticable/TopBanner.vue';

type Props = {
  hideRouteHeader?: boolean;
};

withDefaults(defineProps<Props>(), {
  hideRouteHeader: false,
});

const route = useRoute();
const router = useRouter();
const currentUserStore = useCurrentUserStore();
const teamStore = useTeamStore();

const ready = ref(false);

const accountDetails = computed(() => currentUserStore.currentUserAccountDetails);

const routeHeader = computed(() => {
  const routeName = route.name as string;
  if (!routeName) return '';

  if (routeName.includes('Detail')) {
    return routeName.replace('Detail', '');
  } else if (routeName.includes('New')) {
    return routeName.replace('New', '');
  } else if (routeName.includes('Edit')) {
    return routeName.replace('Edit', '');
  } else if (routeName.includes('Calendar')) {
    return routeName.replace('Calendar', '');
  }
  return routeName;
});

const showHelp = (): boolean => {
  return ![
    'Dashboard',
    'Dashboard V2',
    'Settings',
    'Integrations',
    'CandidateNew',
    'CandidateEdit',
    'Scorecard',
    'Humaniz University',
    'Agent Pipeline Review',
    'Pipeline Reviews',
    'Pipeline Reviews Detail',
    'Pipeline Reviews Edit',
    'Jobs',
    'Product Updates',
  ].includes(route.name as string);
};

const getUserDetails = async (): Promise<void> => {
  await currentUserStore.loadCurrentUserAccountDetails();

  if (accountDetails.value.role === 'Agent' && route.path === '/dashboard') {
    await router.push('/agents/onboarding');
  }

  if (accountDetails.value.role !== 'Agent' && accountDetails.value.isOnboardingCompleted === false) {
    if (accountDetails.value.featureFlags?.hummer) {
      await router.push('/start');
    } else {
      await router.push('/welcome');
    }
  }
};

const getTeamSetup = async (): Promise<void> => {
  const res = await fetch('/api/setup/1');
  const data = await res.json();
  teamStore.setTeamSetupProgress(data.progress);
};

onMounted(async () => {
  window.document.body.classList.add('lg:overflow-hidden');
  await getUserDetails();
  await getTeamSetup();
  ready.value = true;
});
</script>

<style>
@reference "tailwindcss";
.layout-container {
  grid-template-rows: 70px calc(100vh - 70px);
  grid-template-columns: minmax(0, 1fr);
}

.notification {
  @apply animate-bounce rounded-full p-1 text-xs text-white flex items-center justify-center bg-rose-600 absolute top-0 right-0 -mt-1 mr-1 border-2 border-slate-100 box-border;
}

.icon-link {
  @apply relative flex items-center px-2 py-2 text-sm leading-5 font-medium text-gray-600 rounded-md hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:text-gray-900 focus:bg-gray-100 transition ease-in-out duration-150;
}
</style>
